import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, AlertTriangle, Loader2, Building2, Users, Zap } from "lucide-react";
import { ClientForm, clientToForm, clientPayload, eur } from "./FormazionePage";
import { Switch } from "../components/ui/switch";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");
const plus2y = (d) => { if (!d) return ""; const [y, m, g] = d.split("-"); return `${+y + 2}-${m}-${g}`; };
const EMPTY_P = { nominativo: "", data_corso: "", data_prossimo: "", certificato: false };

const PersonDialog = ({ open, onClose, initial, onSave }) => {
  const [f, setF] = useState(EMPTY_P);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(initial || EMPTY_P); }, [open, initial]);
  const setCorso = (v) => setF((p) => ({ ...p, data_corso: v, data_prossimo: !p.data_prossimo || p.data_prossimo === plus2y(p.data_corso) ? plus2y(v) : p.data_prossimo }));
  const save = async () => { setSaving(true); try { await onSave(f); } finally { setSaving(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{initial ? "Modifica persona formata" : "Nuova persona formata"}</DialogTitle>
          <DialogDescription>Il prossimo corso viene proposto a 2 anni dalla data del corso, ma puoi modificarlo.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="nx-label">Nominativo *</Label><Input data-testid="person-nominativo" value={f.nominativo} onChange={(e) => setF({ ...f, nominativo: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="nx-label">Data del corso *</Label><Input data-testid="person-data-corso" type="date" value={f.data_corso} onChange={(e) => setCorso(e.target.value)} className="mt-1.5" /></div>
          <div><Label className="nx-label">Data prossimo corso</Label><Input data-testid="person-data-prossimo" type="date" value={f.data_prossimo} onChange={(e) => setF({ ...f, data_prossimo: e.target.value })} className="mt-1.5" /></div>
          <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2.5 cursor-pointer">
            <span className="flex items-center gap-2 text-sm text-slate-700"><Zap className="h-4 w-4 text-[#C6AD76]" /> <span><b>Certificato</b><span className="block text-xs text-slate-500">La persona è già un operatore: mostra la saetta nell'elenco.</span></span></span>
            <Switch data-testid="person-certificato" checked={!!f.certificato} onCheckedChange={(v) => setF({ ...f, certificato: !!v })} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button data-testid="person-save" onClick={save} disabled={saving || !f.nominativo.trim() || !f.data_corso}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function FormazioneClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.staff_role !== "operatore";
  const [c, setC] = useState(null);
  const [editClient, setEditClient] = useState(false);
  const [form, setForm] = useState(null);
  const [personDlg, setPersonDlg] = useState({ open: false, person: null });

  const load = useCallback(() => api.get(`/formazione/clienti/${id}`).then((r) => setC(r.data)).catch(() => navigate("/operativa/formazione")), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const saveClient = async () => {
    try { const { data } = await api.put(`/formazione/clienti/${id}`, clientPayload(form)); setC(data); setEditClient(false); toast.success("Cliente aggiornato"); }
    catch (err) { toast.error("Non aggiornato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const deleteClient = async () => {
    if (!window.confirm(`Eliminare il cliente "${c.nominativo}" e tutte le persone formate?`)) return;
    try { await api.delete(`/formazione/clienti/${id}`); toast.success("Cliente eliminato"); navigate("/operativa/formazione"); }
    catch (err) { toast.error("Non eliminato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const savePerson = async (f) => {
    const body = { nominativo: f.nominativo, data_corso: f.data_corso, data_prossimo: f.data_prossimo || null, certificato: !!f.certificato };
    try {
      if (personDlg.person) await api.put(`/formazione/persone/${personDlg.person.id}`, body);
      else await api.post(`/formazione/clienti/${id}/persone`, body);
      toast.success(personDlg.person ? "Persona aggiornata" : "Persona aggiunta");
      setPersonDlg({ open: false, person: null }); load();
    } catch (err) { toast.error("Non salvato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };
  const deletePerson = async (p) => {
    if (!window.confirm(`Eliminare "${p.nominativo}"?`)) return;
    try { await api.delete(`/formazione/persone/${p.id}`); toast.success("Persona eliminata"); load(); }
    catch (err) { toast.error("Non eliminata", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  if (!c) return <AppLayout crumbLabel="Cliente"><div className="max-w-5xl mx-auto space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-12 w-full" />)}</div></AppLayout>;

  return (
    <AppLayout crumbLabel={c.nominativo}>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 data-testid="formazione-client-title" className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2"><GraduationCap className="h-7 w-7 text-primary" /> {c.nominativo}</h1>
            <p className="text-sm text-slate-500">Scheda cliente formazione {c.prodae && <>· PRODAE <b>{c.prodae}</b></>}</p>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button data-testid="formazione-client-edit" variant="outline" className="gap-2" onClick={() => { setForm(clientToForm(c)); setEditClient(true); }}><Pencil className="h-4 w-4" /> Modifica</Button>
              <Button data-testid="formazione-client-delete" variant="destructive" className="gap-2" onClick={deleteClient}><Trash2 className="h-4 w-4" /> Elimina</Button>
            </div>
          )}
        </div>

        <Card className="border-slate-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Dati cliente</h2>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[["N° PRODAE", c.prodae], ["Ragione Sociale / Nominativo", c.nominativo], ["Indirizzo", c.indirizzo], ["Mail", c.email], ["Telefono", c.telefono], ["Prezzo BLSD", eur(c.prezzo_blsd)], ["Prezzo Agg. BLSD", eur(c.prezzo_agg_blsd)]].map(([l, v]) => (
              <div key={l} className={l === "Indirizzo" ? "sm:col-span-2" : ""} data-testid={`client-field-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}><dt className="nx-label">{l}</dt><dd className="text-slate-800 mt-0.5">{v || "—"}</dd></div>
            ))}
          </dl>
        </Card>

        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" /><h2 className="font-semibold text-slate-800">Persone formate</h2>
            <span className="text-xs text-slate-400 ml-1">{c.n_certificati} certificati{c.n_in_scadenza > 0 && <span className="text-red-600 font-semibold"> · {c.n_in_scadenza} in scadenza</span>}</span>
            <Button data-testid="person-add" size="sm" className="ml-auto gap-1" onClick={() => setPersonDlg({ open: true, person: null })}><Plus className="h-4 w-4" /> Aggiungi persona</Button>
          </div>
          {c.persone.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">Nessuna persona formata registrata.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Nominativo</TableHead><TableHead>Data corso</TableHead><TableHead>Prossimo corso</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {c.persone.map((p) => (
                  <TableRow key={p.id} data-testid={`person-row-${p.id}`} className={p.in_scadenza ? "bg-red-50/50" : ""}>
                    <TableCell className="font-semibold text-slate-800">
                      <span className="inline-flex items-center gap-1.5">
                        {p.certificato && <Zap data-testid={`person-certificato-${p.id}`} className="h-4 w-4 text-[#C6AD76] fill-[#C6AD76]" title="Certificato — già operatore" aria-label="Certificato" />}
                        {p.nominativo}
                      </span>
                    </TableCell>
                    <TableCell>{fmtDate(p.data_corso)}</TableCell>
                    <TableCell className={p.in_scadenza ? "text-red-700 font-semibold" : ""}>{fmtDate(p.data_prossimo)}</TableCell>
                    <TableCell>
                      {p.in_scadenza ? (
                        <span data-testid={`person-alert-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 px-2.5 py-0.5 text-xs font-semibold">
                          <AlertTriangle className="h-3.5 w-3.5" /> {p.scaduto ? "Scaduto" : `Scade tra ${p.giorni_alla_scadenza} gg`}
                        </span>
                      ) : <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">In regola</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {canEdit && (<>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-primary" data-testid={`person-edit-${p.id}`} onClick={() => setPersonDlg({ open: true, person: p })}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid={`person-delete-${p.id}`} onClick={() => deletePerson(p)}><Trash2 className="h-4 w-4" /></Button>
                      </>)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <PersonDialog open={personDlg.open} initial={personDlg.person ? { nominativo: personDlg.person.nominativo, data_corso: personDlg.person.data_corso, data_prossimo: personDlg.person.data_prossimo, certificato: !!personDlg.person.certificato } : null}
          onClose={() => setPersonDlg({ open: false, person: null })} onSave={savePerson} />

        <Dialog open={editClient} onOpenChange={setEditClient}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Modifica cliente</DialogTitle><DialogDescription>Aggiorna i dati anagrafici.</DialogDescription></DialogHeader>
            {form && <ClientForm form={form} setForm={setForm} />}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditClient(false)}>Annulla</Button>
              <Button data-testid="formazione-client-save" onClick={saveClient} disabled={!form?.nominativo.trim()}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
